import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, UserPlus } from "lucide-react";
import api from "../lib/api";
import { useCoachSetup } from "../context/CoachSetupContext";
import { ABOUT_MAX, COACH_PROFILE_UNVERIFIED_NOTE, HEADLINE_MAX } from "../lib/coachProfile";
import { COACH_HOME } from "../lib/homePath";
import { COACH_SETUP_INVITE_ANCHOR } from "../lib/coachSetup";
import AddPlayerCard from "../components/AddPlayerCard";
import { SecondaryAction, ActionButton, Field, DictationControl } from "../components/kit";
import { getVoiceStatus } from "../lib/voice";
import { leaveSetUpTo } from "../lib/tour";

/**
 * The coach's short first-run set-up (issue #755, wave 1 of epic #749) —
 * an insertion before `/coach-portal`, not a replacement (decision 14).
 * Rendered instead of `/coach-portal` on first sign-in via
 * `CoachSetupGate.jsx`, exactly the way `OnboardingGate.jsx`/
 * `OnboardingNew.jsx` insert the player walkthrough — composed the same
 * way that sibling is composed (per this issue's own instruction): the
 * kit's presentational pieces, the app's real `TopBar` supplied by
 * `Layout.jsx`'s `CHROMELESS_ROUTES` gate (not a second kit `<TopBar>` —
 * see `OnboardingNew.jsx`'s header for why stacking one here would be
 * wrong), and the two fields plus the invite step REUSED rather than
 * rebuilt: `HEADLINE_MAX`/`ABOUT_MAX`/`COACH_PROFILE_UNVERIFIED_NOTE` from
 * `lib/coachProfile.js` (the same limits `CoachProfileCard.jsx` enforces
 * in Settings), and `AddPlayerCard` itself (`embedded`) for the invite
 * form — never a second copy of either.
 *
 * BOTH FIELDS OPTIONAL, NO COMPLETENESS GATE. Unlike the player
 * walkthrough's Continue (disabled until the step is complete), this
 * screen's "Continue to your coach portal" is always enabled — the
 * drawing (`CoachSetUp.dc.html`) shows no disabled state, and the issue's
 * own words are explicit: "Both can wait; everything here is optional".
 *
 * ONE PUT DOES BOTH JOBS. `PUT /coach-profiles/me` (already shipped by
 * #770) saves whatever was typed AND, with `coach_setup_completed: true`,
 * stamps the marker in the same call — see that route's own comment.
 * Both `skipProfile` and `continueToPortal` below send that flag; the
 * literal difference between "skip" and "continue" is only whether the
 * two text fields are typed in the request body, which is the honest
 * behaviour already documented on the button: "saves the profile (if
 * anything was typed), stamps the marker, and navigates."
 *
 * THE INVITE STEP HAS NO STAMP OF ITS OWN. Sending an invite (via the
 * embedded `AddPlayerCard`) does not, by itself, complete the set-up —
 * only "Continue to your coach portal" or either "Skip" does. A coach who
 * sends an invite and then still clicks Continue stamps normally; nothing
 * here double-submits or double-stamps.
 *
 * WHY "SKIP — INVITE LATER" SAVES THE PROFILE TOO, THE SAME AS CONTINUE.
 * This is one screen, not two steps — both skip links and the invite card
 * are visible at once (`CoachSetUp.dc.html`), so "Skip — invite later"
 * cannot mean "go back and fill in the profile fields" the way a paged
 * wizard's per-step skip would; there is no later page to return to. Its
 * whole job is to let a coach who typed a headline/about but does not
 * want to invite anyone right now leave without losing that text — so it
 * calls the SAME save-and-stamp path as Continue. Leaving it un-stamped
 * would strand the coach: `CoachSetupGate.jsx` gates every protected
 * route, `/coach-portal` included, so an unstamped "skip" would bounce
 * straight back here.
 *
 * `id={COACH_SETUP_INVITE_ANCHOR}` ON THE INVITE SECTION (#756). The empty
 * coach portal's link and gold action both need to open "the invite step
 * of the coach set-up", and this screen has no separate step to send them
 * to (see `lib/coachSetup.js`'s own comment) — so the anchor id is the
 * agreed destination, scrolled to for free by `ScrollToTop.jsx`'s existing
 * `#hash` handling.
 */
export default function CoachSetUp() {
  const nav = useNavigate();
  const { applyStatus, profile } = useCoachSetup();
  // Issue #897 — pre-filled from the profile `CoachSetupContext` already
  // fetched to decide `required` (that fetch has resolved by the time this
  // screen mounts: `CoachSetupGate.jsx` shows its own loading state until
  // then, and this screen only ever renders as that gate's `children`).
  // Reopening this screen — "Invite a player" from the portal, or simply
  // navigating back, both legal per `CoachSetupGate.jsx`'s own header —
  // used to start these two fields at `""` unconditionally, and
  // `continueToPortal`/`skipProfile` below PUT whatever is in them; an
  // untouched `""` is "clear it" to the API (`coachProfiles.ts`'s
  // `normalise`), which silently erased a saved headline/about. A brand
  // new coach has no `profile` yet (`useCoachSetup`'s `EMPTY`/pre-fetch
  // value is `null`), so `?? ""` keeps the first-run screen exactly as
  // blank as it always was.
  //
  // #897 FIX ROUND (B1/B2) — SEEDING IS FOR DISPLAY ONLY, NEVER FOR THE
  // WRITE. The pre-fill above only closes the gap for a coach who never
  // touches the fields again before Continue — it is itself a SNAPSHOT,
  // taken once, of whatever `profile` this screen's single mount saw. Two
  // ways that snapshot can be wrong by the time Continue is clicked, and
  // both were still data loss with the naive "always send headline/about"
  // shape this screen shipped with:
  //   B1 — the fetch behind `profile` can fail (a 503, a flaked network):
  //        `CoachSetupContext.js` fails OPEN (`required: false`, `profile`
  //        stays `null`), so the fields render blank while a real, saved
  //        profile still sits in the database. Sending that blank as an
  //        edit would erase it.
  //   B2 — the coach can edit their profile ELSEWHERE (Settings'
  //        `CoachProfileCard`) after this screen's one fetch already ran,
  //        then reach `/coach-setup` again (still the same SPA session,
  //        so the context is not re-fetched) via "Invite a player".
  //        Sending the STALE snapshot as an edit would silently revert
  //        the newer Settings write.
  // Both are the same shape: this screen has no business asserting an
  // opinion about a field it never actually changed. So each field now
  // tracks whether the COACH typed into it, this screen's own session,
  // and `continueToPortal` below sends only the ones that are true —
  // never the pre-fill value on its own. An untouched field is OMITTED
  // from the PUT body entirely, which `coachProfileUpdateSchema`/
  // `normalise` (api/src/routes/coachProfiles.ts) already read as "leave
  // it" (the same rule that always protected a brand-new coach's other,
  // unrelated profile fields — years coaching, qualifications, and so on
  // — which this screen has never asked about and never sent).
  const [headline, setHeadline] = useState(() => profile?.headline ?? "");
  const [about, setAbout] = useState(() => profile?.about ?? "");
  const [headlineEdited, setHeadlineEdited] = useState(false);
  const [aboutEdited, setAboutEdited] = useState(false);
  const [busy, setBusy] = useState(false);

  const onHeadlineChange = (e) => {
    setHeadline(e.target.value);
    setHeadlineEdited(true);
  };
  const onAboutChange = (e) => {
    setAbout(e.target.value);
    setAboutEdited(true);
  };
  // D235 — a coach may dictate the "About you" box. A transcript is the
  // coach's own words landing in the box, so it counts as an edit (the
  // untouched-field rule above must not drop it from the PUT body).
  const onAboutDictated = (v) => {
    setAbout(v);
    setAboutEdited(true);
  };
  const aboutRef = useRef(null);
  const [voiceStatus, setVoiceStatus] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getVoiceStatus().then((s) => {
      if (!cancelled) setVoiceStatus(s);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const finish = async (extra) => {
    setBusy(true);
    try {
      const { data } = await api.put("/coach-profiles/me", {
        ...extra,
        coach_setup_completed: true,
      });
      applyStatus(data);
      // #1514 (D406): a device that has not seen the coach tour goes to it
      // first; its Skip and last card continue to the coach portal.
      leaveSetUpTo(nav, "coach", COACH_HOME);
    } catch (e) {
      console.error("CoachSetUp: failed to save/stamp coach set-up", e);
      toast.error("We couldn't save that just now — please try again.");
    } finally {
      setBusy(false);
    }
  };

  // "Skip this" (the profile fields) — stamps the marker, saves nothing.
  const skipProfile = () => finish({});

  // "Continue to your coach portal" (and "Skip — invite later", which
  // calls this same function — see that button's own comment below) —
  // saves ONLY the field(s) the coach actually edited in THIS session
  // (#897 fix round, B1/B2 — see the state declarations above for the
  // full reasoning). A field never typed into is left out of the body
  // entirely, not sent as its pre-filled or blank display value, so the
  // route's own "absent means leave it" rule is what protects it — the
  // same protection a first-time coach's other profile fields already
  // had, extended to these two.
  const continueToPortal = () => {
    const extra = {};
    if (headlineEdited) extra.headline = headline;
    if (aboutEdited) extra.about = about;
    return finish(extra);
  };

  return (
    // Issue #792 — `space-y-6` (Tailwind, `rem`) rendered this page's own
    // stack gap at 25.5px against CoachSetUp.dc.html:59's drawn 14px
    // (28.5px once the review's PR set the text-size control to Large —
    // the finding this issue is built on). `.nw-page-stack` (index.css)
    // is fixed px from `--nw-space-stack`. PR #826 review fix round: the
    // About-you `space-y-1` and the invite card's own `gap-2` are now
    // fixed too — see each site's own comment. Issue #827: the header-block
    // mt-1/mt-2 (below) is now fixed too. The "Skip this" row's `gap-3`
    // is untouched too: CoachSetUp.dc.html:78 draws that row as
    // `justify-content: space-between` with no `gap` of its own — with
    // exactly two flex children pinned to opposite ends, `gap-3` has no
    // rendered effect unless the row is narrow enough to need it, so
    // there is nothing here to measure against a drawn value.
    <div className="nw-page-stack" data-testid="coach-setup-page">
      {/* A marker only THIS component renders — the epic #749 per-screen-
          slice leg-assertion rule (#753's assertSignUpScreenMatchesLeg,
          #754's assertSetUpScreenMatchesLeg, this screen's own
          screenIsOnForLeg("coach-setup") in the journey suite). Every
          other testid on this page could in principle be reused by a
          future shipped screen; this one exists nowhere else. */}
      <span data-testid="coach-setup-new-screen" className="sr-only" aria-hidden="true" />

      {/* Issue #827 — `mt-1`/`mt-2` (Tailwind, `rem`) rendered this block's
          gaps at 8.75/8.5px, moving with the text-size control, against
          CoachSetUp.dc.html:61's drawn `gap: 6px` — the same shape #826
          already fixed for OnboardingNew's own header block
          (OnboardingNew.jsx's own comment). `.nw-stack-tight` (index.css)
          is fixed px from the existing `--nw-space-tight` token (6px), the
          same class the "rest lives in Settings" box below already uses.
          THE EYEBROW IS A `<div>`, NOT A `<span>` (measured live,
          `frontend/e2e/spacing-tokens.spec.js`): an inline element as the
          stack's first child sits in its own anonymous line box, whose
          height is padded out to the CONTAINER's inherited line-height
          (`html`'s `line-height: 1.5` x `--nw-base-font`, i.e. it moves
          with the text-size control) rather than the eyebrow's own fixed
          10px/1.5 metrics — so the eyebrow -> title gap measured 9.5px at
          Compact and 11.5px at Default/Large, not the drawn, static 6px,
          even with the margin-top token correctly applied to the title.
          Block removes the inline formatting context (and its
          root-font-dependent strut) entirely; `EmptyState.jsx`'s own
          `eyebrow` already renders as a `<div>` for the same class, so
          this matches established practice rather than inventing one. */}
      <div className="nw-stack-tight">
        <div className="nw-eyebrow" data-testid="coach-setup-step-label">
          Coach set-up · one short step
        </div>
        <h1 className="nw-title">Introduce yourself</h1>
        <p className="nw-lede">
          Two lines about you, and your first player if you have one in mind. Both can wait; everything
          here is optional and is shown to players you're linked to.
        </p>
      </div>

      <Field
        label="Headline"
        value={headline}
        onChange={onHeadlineChange}
        maxLength={HEADLINE_MAX}
        placeholder="e.g. Batting coach, Auckland"
        data-testid="coach-setup-headline"
      />

      {/* PR #826 review fold-in — `space-y-1` (Tailwind, `rem`) rendered
          this group's gap at 4.25px, moving, against CoachSetUp.dc.html:72's
          drawn `gap: 4px` (label, textarea, the 0/1500 counter, all three).
          `.nw-stack-xtight` (index.css, new `--nw-space-xtight` token) is
          fixed px. */}
      <div className="nw-stack-xtight">
        {/* Review round 3, final narrow round (A3), F9: real <label htmlFor>,
            same fix as kit/Field.jsx's own (#902/#907) — explicit
            display: "block" keeps the layout byte-identical (a <label>
            defaults to inline where the <div> it replaces defaulted to
            block). */}
        <label htmlFor="coach-setup-about" className="nw-label" style={{ display: "block" }}>About you</label>
        <textarea
          className="nw-kit-input"
          style={{
            width: "100%",
            minHeight: 96,
            borderRadius: "var(--nw-radius-control)",
            border: "1px solid var(--nw-line)",
            background: "var(--nw-surface)",
            padding: 12,
            fontSize: 15,
            lineHeight: 1.45,
            color: "var(--nw-ink)",
            resize: "vertical",
          }}
          value={about}
          onChange={onAboutChange}
          maxLength={ABOUT_MAX}
          placeholder="A short introduction — how you work, who you work with."
          id="coach-setup-about"
          data-testid="coach-setup-about"
          ref={aboutRef}
        />
        <DictationControl
          idPrefix="coach-setup-about-mic"
          boxLabel="About you"
          value={about}
          onChange={onAboutDictated}
          textareaRef={aboutRef}
          status={voiceStatus}
          maxLength={ABOUT_MAX}
        />
        {/* Fix round F5, corrected on review: `text-xs` is Tailwind's
            rem-scaled named size — 12.75px against the app's 17px root,
            and it moves with the text-size control, forbidden by
            `index.css:145-147`. The house pattern is a ramp class plus an
            inline style for the one property that differs — `.nw-help`
            already gives 12px/1.5 line-height (18px, matching what this
            counter renders today) and the drawn muted colour; only the
            colour token differs here (faint, not muted), so that's the
            only override. A literal `fontSize: 12` (the first fix)
            doesn't move with the kit — this does. */}
        <p className="nw-help" style={{ color: "var(--nw-ink-faint)" }}>
          {about.length}/{ABOUT_MAX}
        </p>
      </div>

      <div className="flex items-center justify-between gap-3">
        {/* Fix round F1: the drawing's sentence is "Players read this
            under your name as <the note, quoted>" — the note itself is
            the shared, verbatim constant (`lib/coachProfile.js`, read by
            three other surfaces; its own header forbids editing it for
            one caller's wording), so the framing is added HERE, at the
            call site, not inside the constant. */}
        <span className="nw-help">
          Players read this under your name as &quot;{COACH_PROFILE_UNVERIFIED_NOTE}&quot;
        </span>
        {/* `variant="link"` is the kit's bare-text button — the right
            layout for this drawn element — but its default colour is
            navy (`SecondaryAction.jsx`'s link branch); the drawing's two
            Skip labels are muted slate at 13px
            (`CoachSetUp.dc.html:80,95`), matching R-003's rule that
            Continue is the ONLY gold/emphasised element on this screen.
            `style` is an exposed override on the kit component, not a
            fork of it. */}
        <SecondaryAction
          variant="link"
          onClick={skipProfile}
          disabled={busy}
          data-testid="coach-setup-skip-profile"
          style={{ whiteSpace: "nowrap", color: "var(--nw-ink-muted)", fontSize: 13 }}
        >
          Skip this
        </SecondaryAction>
      </div>

      <div style={{ height: 1, background: "var(--nw-line)" }} />

      {/* Issue #792 — this card's own `space-y-2` (Tailwind, `rem`)
          rendered its internal gap at 8.5px against CoachSetUp.dc.html:85's
          drawn 8px, and moved with the text-size control — the "Invite
          card gap" measurement the issue names. `.nw-stack-grid`
          (index.css) is fixed px from `--nw-space-grid`, the existing
          8px token; the card's own `padding: 14` literal already matched
          the drawing and needed no change. */}
      <div
        id={COACH_SETUP_INVITE_ANCHOR}
        className="nw-stack-grid"
        style={{
          padding: 14,
          background: "var(--nw-surface)",
          border: "1px solid var(--nw-line)",
          borderRadius: "var(--nw-radius-card)",
        }}
      >
        {/* PR #826 review fold-in — `gap-2` (Tailwind, `rem`) rendered this
            row's gap at 8.5px, moving, against CoachSetUp.dc.html:86's
            drawn `gap: 8px`. `var(--nw-space-grid)` (the existing 8px
            token) is fixed px — a flex-row gap, not a vertical stack, so
            this stays an inline style. */}
        <div className="flex items-center" style={{ gap: "var(--nw-space-grid)" }}>
          <UserPlus size={18} color="var(--nw-navy)" aria-hidden="true" />
          {/* Issue #792's second sub-pixel leftover: `.font-display` sets
              `letter-spacing: -0.02em` (-0.3px at 15px) — CoachSetUp.dc.html:88
              draws this heading with no letter-spacing at all. `.font-display`
              is kept for the Manrope font-family/weight; only the tracking is
              overridden back to normal, at the call site rather than by
              forking the class (`.font-display` is used correctly elsewhere). */}
          <div
            className="font-display font-bold text-[15px]"
            style={{ color: "var(--nw-ink)", letterSpacing: "normal" }}
          >
            Invite your first player
          </div>
          <span className="ml-auto text-[11px] font-semibold" style={{ color: "var(--nw-ink-muted)" }}>
            Optional
          </span>
        </div>
        {/* Fix round F5: `.nw-help` (12px/400/1.5/`--nw-ink-muted`) is the
            kit's own fixed-px class for exactly this shape — replaces the
            rem-scaled `text-xs`. */}
        <p className="nw-help">
          Invite a player by their account email. They'll see the invite in their Settings and choose
          whether to accept — you can't see any of their cricket data unless they do.
        </p>
        {/* Issue #830 — `kitType` is the fix for the finding filed against
            THIS embed: both of `/coach-setup`'s moving marked elements
            traced to AddPlayerCard's shadcn `Input`/`Button`, still
            Tailwind `rem`. `embedded` is unchanged (still drops the
            card/heading chrome this section already supplies);
            `Settings.jsx`'s own call site, `<AddPlayerCard />`, sets
            neither prop, so its rendering is unchanged by this.

            Audit G6 (#869), D173.C2: "Skip — invite later" moves off its
            own row and into Send's row (`CoachSetUp.dc.html:93-96`,
            `justify-content: space-between`) via `sendRowExtra` —
            AddPlayerCard's own prop for exactly this, since only it knows
            where that row is. The handler, disabled state and testid are
            unchanged: still `continueToPortal`, still gated on `busy`,
            still `coach-setup-skip-invite`. */}
        <AddPlayerCard
          embedded
          kitType
          sendRowExtra={
            <SecondaryAction
              variant="link"
              onClick={continueToPortal}
              disabled={busy}
              data-testid="coach-setup-skip-invite"
              style={{ color: "var(--nw-ink-muted)", fontSize: 13, whiteSpace: "nowrap" }}
            >
              Skip — invite later
            </SecondaryAction>
          }
        />
      </div>

      {/* Fix round F3/F4: `CoachSetUp.dc.html:99-102` draws a CHEVRON
          (`M9 18l6-6-6-6`, no shaft) — `ChevronRight`, not the full-shafted
          `ArrowRight` this used to import. Issue #793: the local
          `width: "100%"` F4 added here is gone — `ActionButton` spans its
          row by default now (kit's own `display:flex; width:100%`), so a
          caller alone in a block container like this one gets the drawn
          width without diagnosing its own layout. Issue #792's first
          sub-pixel leftover: `CoachSetUp.dc.html:101` draws this chevron
          at `stroke-width="2.5"`; lucide's default is 2 and nothing here
          overrode it.
          Round 5 conformance, D27 (issue #864) — this chevron used to hard-code
          `color="#FFFFFF"`, matching the label's own now-fixed white default;
          both were wrong (`CoachSetUp.dc.html:99-102` draws the chevron in
          the same near-black ink as the label). No explicit colour needed
          here any more — the SVG's `currentColor` stroke inherits the
          button's own ink text colour. */}
      <ActionButton onClick={continueToPortal} disabled={busy} data-testid="coach-setup-continue">
        {busy ? (
          "Saving…"
        ) : (
          <>
            Continue to your coach portal
            <ChevronRight size={20} strokeWidth={2.5} style={{ marginLeft: 10 }} aria-hidden="true" />
          </>
        )}
      </ActionButton>

      {/* Issue #792 — this box's own `space-y-1.5` (Tailwind, `rem`)
          rendered its internal gap at 6.38px against CoachSetUp.dc.html:104's
          drawn 6px — the "Rest lives in Settings" measurement the issue
          names. `.nw-stack-tight` (index.css) is fixed px from the new
          `--nw-space-tight` token. */}
      <div
        className="nw-stack-tight"
        style={{
          padding: 14,
          background: "var(--nw-surface)",
          border: "1px solid var(--nw-line)",
          borderLeft: "3px solid var(--nw-navy)",
          borderRadius: "var(--nw-radius-card)",
        }}
      >
        <div className="nw-section">The rest lives in Settings</div>
        {/* Fix round F2: `CoachSetUp.dc.html:106` colours this paragraph
            `#475569` — `--nw-ink-lede`, not `--nw-ink-muted` (`#64748B`),
            which this used to carry. */}
        <p className="text-[13px]" style={{ color: "var(--nw-ink-lede)", lineHeight: 1.5 }}>
          Years coaching, highest level played, what you specialise in, qualifications, region, teams
          you've coached and a photo are all on your coaching profile in Settings, where the two lines
          above are too. Nothing else is asked at the door.
        </p>
      </div>
    </div>
  );
}
